import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  LineController
} from 'chart.js'
import './ConsumptionChart.css'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  LineController
)

function ConsumptionChart({ consumption, priceData, includeVAT = true }) {
  const vatMultiplier = includeVAT ? 1.21 : 1.0
  if (!consumption || consumption.length === 0) {
    return <div className="chart-empty">Nėra duomenų</div>
  }

  // Determine date range
  const dates = consumption.map(r => new Date(r.datetime))
  const minDate = new Date(Math.min(...dates))
  const maxDate = new Date(Math.max(...dates))
  const daysDiff = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24))

  // Determine aggregation level
  let aggregationLevel = 'hour' // default
  let aggregationLabel = 'Valandinis'
  
  if (daysDiff <= 1) {
    aggregationLevel = 'hour'
    aggregationLabel = 'Valandinis suvartojimas ir kaina'
  } else if (daysDiff <= 31) {
    aggregationLevel = 'day'
    aggregationLabel = 'Dieninis suvartojimas ir kaina'
  } else {
    aggregationLevel = 'month'
    aggregationLabel = 'Mėnesinis suvartojimas ir kaina'
  }

  // Aggregate data based on level
  const aggregatedData = {}
  const samplePrice = 0.10

  consumption.forEach(record => {
    const date = new Date(record.datetime)
    let key

    if (aggregationLevel === 'hour') {
      const hour = date.getHours()
      key = `${hour.toString().padStart(2, '0')}:00`
    } else if (aggregationLevel === 'day') {
      key = date.toISOString().split('T')[0]
    } else {
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      key = `${year}-${month}`
    }

    if (!aggregatedData[key]) {
      aggregatedData[key] = { 
        purchase: 0, 
        sale: 0,
        purchaseCost: 0,
        saleRevenue: 0
      }
    }

    // Normalize lookup key for priceData
    const dt = record.datetime
    const lookupKey = dt.substring(0, 10) + ' ' + dt.substring(11, 19)
    const currentPrice = (priceData?.[lookupKey] || samplePrice) * vatMultiplier

    if (record.type === 'P+') {
      aggregatedData[key].purchase += record.kwh
      aggregatedData[key].purchaseCost += record.kwh * currentPrice
    } else if (record.type === 'P-') {
      aggregatedData[key].sale += record.kwh
      aggregatedData[key].saleRevenue += record.kwh * currentPrice
    }
  })

  // Sort keys and format labels
  const sortedKeys = Object.keys(aggregatedData).sort()
  const labels = sortedKeys.map(key => {
    if (aggregationLevel === 'hour') {
      return key
    } else if (aggregationLevel === 'day') {
      const date = new Date(key)
      return date.toLocaleDateString('lt-LT', { month: 'short', day: 'numeric' })
    } else {
      const [year, month] = key.split('-')
      const date = new Date(year, parseInt(month) - 1)
      return date.toLocaleDateString('lt-LT', { year: 'numeric', month: 'long' })
    }
  })

  // Calculate weighted prices for each period
  const purchasePrices = sortedKeys.map(key => {
    const d = aggregatedData[key]
    return d.purchase > 0 ? d.purchaseCost / d.purchase : null
  })

  const salePrices = sortedKeys.map(key => {
    const d = aggregatedData[key]
    return d.sale > 0 ? d.saleRevenue / d.sale : null
  })

  const chartData = {
    labels,
    datasets: [
      {
        type: 'bar',
        label: 'Pirkimas (P+)',
        data: sortedKeys.map(key => aggregatedData[key].purchase),
        backgroundColor: 'rgba(37, 99, 235, 0.6)',
        borderColor: 'rgb(37, 99, 235)',
        borderWidth: 1,
        yAxisID: 'y'
      },
      {
        type: 'bar',
        label: 'Pardavimas (P-)',
        data: sortedKeys.map(key => aggregatedData[key].sale),
        backgroundColor: 'rgba(16, 185, 129, 0.6)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 1,
        yAxisID: 'y'
      },
      {
        type: 'line',
        label: 'Svertinė pirkimo kaina',
        data: purchasePrices,
        borderColor: '#f59e0b',
        backgroundColor: '#f59e0b',
        borderWidth: 2,
        pointRadius: 3,
        yAxisID: 'yPrice',
        tension: 0.3
      },
      {
        type: 'line',
        label: 'Svertinė pardavimo kaina',
        data: salePrices,
        borderColor: '#8b5cf6',
        backgroundColor: '#8b5cf6',
        borderWidth: 2,
        pointRadius: 3,
        yAxisID: 'yPrice',
        tension: 0.3
      }
    ]
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: {
          color: '#f1f5f9',
          usePointStyle: true
        }
      },
      title: {
        display: true,
        text: aggregationLabel,
        color: '#f1f5f9',
        font: {
          size: 14
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || ''
            const val = context.parsed.y
            if (context.dataset.yAxisID === 'yPrice') {
              return `${label}: ${val.toFixed(4)} EUR/kWh (${includeVAT ? 'su PVM' : 'be PVM'})`
            }
            return `${label}: ${val.toFixed(3)} kWh`
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Kiekis (kWh)',
          color: '#94a3b8'
        },
        ticks: {
          color: '#94a3b8'
        },
        grid: {
          color: '#334155'
        }
      },
      yPrice: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: false,
        title: {
          display: true,
          text: 'Kaina (EUR/kWh)',
          color: '#f59e0b'
        },
        ticks: {
          color: '#f59e0b',
          callback: function(value) {
            return value.toFixed(3)
          }
        },
        grid: {
          drawOnChartArea: false // only want grid lines for one axis
        }
      },
      x: {
        ticks: {
          color: '#94a3b8',
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          color: '#334155'
        }
      }
    }
  }

  return (
    <div className="consumption-chart">
      <div className="chart-info">
        <span className="info-badge">
          📅 Laikotarpis: {daysDiff === 0 ? '1 diena' : `${daysDiff + 1} dienos`}
        </span>
        <span className="info-badge">
          📊 Agregacija: {aggregationLevel === 'hour' ? 'Valandinė' : aggregationLevel === 'day' ? 'Dieninė' : 'Mėnesinė'}
        </span>
      </div>
      <div className="chart-container" style={{ height: '400px' }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  )
}

export default ConsumptionChart
